import { useEffect, useState } from "react";
import { Select } from "antd";

import useApiMethods from "../../../lib/useApiMethods";
import { PENALTY_APPEAL } from "../../../constants/api";
import ToastMsg from "../../../components/common/ToastMsg";
import { DialogModal } from "../../../components/ui/dialogModal";

const { Option } = Select;

const APPEAL_TYPES = [
    { value: "penalty_unfair", label: "Penalty is unfair" },
    { value: "attendance_wrong", label: "Attendance record is wrong" },
    { value: "other", label: "Other" },
];

/**
 * Submit a penalty concern (appeal) for a calendar or profile penalty row.
 * `penalty` expects attendance_id, date_display, penalty_type, value_display.
 */
const PenaltyAppealDialog = ({ open, penalty, onClose, onSuccess }) => {
    const { create } = useApiMethods();
    const [appealType, setAppealType] = useState("penalty_unfair");
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open || !penalty) return;
        setAppealType(
            penalty?.penalty_type?.toLowerCase?.().includes("late") ? "penalty_unfair" : "attendance_wrong",
        );
        setReason("");
    }, [open, penalty]);

    const submitAppeal = async () => {
        if (!penalty?.attendance_id) return;
        if ((reason || "").trim().length < 10) {
            ToastMsg("error", "Please enter a reason of at least 10 characters.");
            return;
        }
        setSubmitting(true);
        try {
            const res = await create(PENALTY_APPEAL, {
                attendance_id: penalty.attendance_id,
                appeal_type: appealType,
                reason: reason.trim(),
            });
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Appeal submitted");
                onClose?.();
                onSuccess?.(res);
            } else {
                ToastMsg("error", res?.message || "Failed to submit appeal");
            }
        } catch (err) {
            ToastMsg("error", err?.message || "Failed to submit appeal");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <DialogModal
            open={open}
            onClose={onClose}
            onSave={submitAppeal}
            title="Raise penalty concern"
            closeLabel="Cancel"
            saveLabel="Submit appeal"
            loading={submitting}
        >
            <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                    {penalty?.date_display} · {penalty?.penalty_type} · {penalty?.value_display}
                </p>
                <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Concern type</label>
                    <Select className="w-full mt-1" value={appealType} onChange={setAppealType}>
                        {APPEAL_TYPES.map((t) => (
                            <Option key={t.value} value={t.value}>{t.label}</Option>
                        ))}
                    </Select>
                </div>
                <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Reason</label>
                    <textarea
                        className="mt-1 w-full min-h-[100px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                        placeholder="Explain why this penalty should be reviewed (min 10 characters)"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                    />
                </div>
            </div>
        </DialogModal>
    );
};

export default PenaltyAppealDialog;
